import { v4 as uuidv4 } from 'uuid'
import { DeliveryEnum } from '../interfaces'
import { taxIdTypeIdFromAbbreviation } from './mappers'
import {
  ShipmentGroup,
  ShipmentPieceRow,
} from '../interfaces/Import/ShipmentGroupInterface'

/**
 * Construye el `shipment_list[]` (shape de POST /api/shipment/ y de la importación
 * asíncrona) desde los ShipmentGroup del Review.
 *
 * Extraído de ShipmentBulkUploadReview (deuda TODOS, eng-review E1) como función pura
 * para reusarlo en: el create flow, la validación pre-vuelo (#1) y el mapeo inverso
 * de corrección (Approach A). Una sola fuente evita drift entre code paths.
 *
 * NOTA (lossy): el valor declarado por pieza se colapsa aquí en un agregado
 * (declared_value.amount). El inverso payload→grupo no puede recuperar el valor por
 * pieza individual; ver el mapeo inverso para cómo se reconstruye.
 */

export interface BuildShipmentContext {
  user: any
  businessUnits: any[]
  citiesByBu: any[]
  identificationTypes: any[]
  allowedServices: any[]
  paymentMode: string
  serviceId?: number
  externalManifest?: string
  // Oficinas origen usables del contrato (para resolver el origen POR FILA).
  originBusinessUnits?: any[]
}

export function buildShipmentList(
  gs: ShipmentGroup[],
  ctx: BuildShipmentContext
): { shipment_list: any[] } {
  const {
    user,
    businessUnits,
    citiesByBu,
    identificationTypes,
    allowedServices,
    paymentMode,
    serviceId,
    externalManifest,
    originBusinessUnits = [],
  } = ctx
  const fallbackService = serviceId ?? allowedServices[0]?.serviceID ?? 60
  const externalManifestValue = externalManifest?.trim() || null

  // Origen POR FILA: (1) si la fila trae originStoreCode, la BU de ese buCode;
  // (2) si el contrato tiene 1 sola oficina, esa; (3) fallback al cliente.
  // [F2] Si originStoreCode está seteado pero NO resuelve, devolvemos undefined
  // (NO se sustituye por otra oficina): el origin.store queda vacío y el backend
  // lo rechaza. La validación de fila del Review bloquea antes de llegar aquí.
  const resolveOriginBu = (g: ShipmentGroup) => {
    if (g.originStoreCode) {
      // [F2] Resolver SOLO contra las oficinas origen del contrato. Si el buCode no
      // matchea, devolver undefined (NO sustituir por otra del catálogo global
      // `businessUnits`, que incluye oficinas no autorizadas): origin.store queda
      // vacío y el backend rechaza. La validación del Review bloquea antes de llegar aquí.
      return originBusinessUnits.find((b) => b.buCode === g.originStoreCode)
    }
    if (originBusinessUnits.length === 1) return originBusinessUnits[0]
    return user.client?.businessUnit
  }

  const shipment_list = gs.map((g) => {
    const isHome = g.deliveryType === DeliveryEnum.DELIVERY
    const originBu = resolveOriginBu(g)
    const destBu = businessUnits.find((b) => b.buCode === g.storeCode)
    // cityBuCode es el buCode que citiesByBu (Django, /api/location →
    // default_destination_store) asigna a la ciudad: el store de DESTINO para domicilio.
    // El origen no sale de aquí (viene de user.client.businessUnit). NO cruzarlo contra
    // businessUnits: el cruce daba undefined → store vacío en toda guía a domicilio.
    const cityBuCode = citiesByBu.find(
      (c) => c.locationCode === g.destinationCity
    )?.buCode

    const shipperAbbr = (
      identificationTypes.find(
        (t) => t.taxIdentificationTypeId.toString() === g.shipperDocType
      )?.abbreviationName || ''
    ).replace(/-+$/, '')
    const consigneeAbbr = (
      identificationTypes.find(
        (t) => t.taxIdentificationTypeId.toString() === g.consigneeDocType
      )?.abbreviationName || ''
    ).replace(/-+$/, '')

    return {
      origin: {
        home_pickup: false,
        store: originBu?.buCode || '',
        location: originBu?.location?.locationCode || '',
        address: {
          name: null,
          // La dirección de origen debe ser coherente con origin.store (la oficina
          // elegida por fila), NO la dirección fiscal del cliente cuando difieren.
          line:
            originBu?.location?.address || user.client?.fiscalAddress || 'N/A',
          landmark: null,
          postal_code: originBu?.location?.postalCode || null,
        },
      },
      destination: {
        home_delivery: isHome,
        // Domicilio: enviar la tienda que el front YA resolvió y muestra ("Tienda asignada"
        // = g.storeCode, que viene de cobertura por coordenadas, Maps, selección de ciudad o
        // inferencia al importar). Antes se re-derivaba cityBuCode aquí, lo que hacía que el
        // front mostrara una tienda y el API creara otra cuando la cobertura por coordenadas
        // difería de la tienda por ciudad. cityBuCode queda solo como fallback.
        store: isHome
          ? g.storeCode || cityBuCode || ''
          : destBu?.buCode || g.storeCode,
        location: isHome
          ? g.destinationCity || ''
          : destBu?.location?.locationCode || '',
        ...(isHome && g.latitude != null && g.longitude != null
          ? {
              coordinates: {
                latitude: g.latitude,
                longitude: g.longitude,
              },
            }
          : {}),
        address: {
          name: null,
          line: g.destinationAddress || destBu?.location?.address || '',
          landmark: null,
          postal_code: g.postalCode || null,
        },
      },
      shipper: {
        identification: `${shipperAbbr}-${g.shipperDocNumber}`,
        name: g.shipperName,
        phone:
          (user.client?.listAccountPhone?.[0]?.countryPhoneAccessCode || '') +
          ' ' +
          (user.client?.listAccountPhone?.[0]?.phoneNumber || ''),
        email: user.client?.listAccountEmail?.[0]?.email || undefined,
      },
      consignee: {
        identification: `${consigneeAbbr}-${g.consigneeDocNumber}`,
        name: g.consigneeName,
        phone: `${user.client?.listAccountPhone?.[0]?.countryPhoneAccessCode || '+58'} ${g.consigneePhone}`,
        email: g.consigneeEmail || undefined,
      },
      billing: {
        service: g.serviceId ?? fallbackService,
        payment_mode: parseInt(paymentMode),
        account_code: user.client?.accountCode || '',
      },
      declared_value: {
        amount: g.pieces.reduce(
          (acc, p) => acc + (p.declaredValue || 0) * (p.quantity || 1),
          0
        ),
        currency: 'USD',
        invoice_number: null,
      },
      notes: g.notes || null,
      partner_shipment_id: g.shipmentHeaderReference || null,
      partner_manifest_id: g.externalManifest?.trim() || externalManifestValue,
      pct_discount: null,
      parcels: {
        dimension_unit: 'cm',
        weight_unit: 'kg',
        items: g.pieces.flatMap((p) =>
          Array.from({ length: p.quantity || 1 }, () => ({
            height: p.height || 1,
            width: p.width || 1,
            length: p.length || 1,
            weight: p.weight || 1,
            content_category: parseInt(p.categoryId) || 49,
            content_description: p.productName || null,
          }))
        ),
      },
    }
  })

  return { shipment_list }
}

// ---------------------------------------------------------------------------
// Inverso: payload_json (ShipmentInsertRequestDTO) → ShipmentGroup (Approach A)
// ---------------------------------------------------------------------------

export interface ReverseShipmentContext {
  identificationTypes: any[]
  isCOD?: boolean
}

/** "V-12345678" → { docType: taxIdentificationTypeId, docNumber }. */
function parseIdentification(
  identification: string | undefined,
  identificationTypes: any[]
): { docType: string; docNumber: string } {
  const raw = (identification || '').trim()
  const dash = raw.indexOf('-')
  if (dash < 0) return { docType: '', docNumber: raw }
  const prefix = raw.slice(0, dash).trim()
  const docNumber = raw.slice(dash + 1).trim()
  const id = taxIdTypeIdFromAbbreviation(prefix, identificationTypes)
  return { docType: id ? id.toString() : '', docNumber }
}

/**
 * Reconstruye un ShipmentGroup desde el payload_json serializado de un detalle con
 * error, para reabrirlo en el editor (Review) y corregirlo (Approach A, eng-review E2).
 *
 * LOSSY (documentado en buildShipmentList): el forward expande las piezas por cantidad
 * y colapsa el valor declarado en un agregado. El inverso re-agrupa piezas idénticas y
 * distribuye el agregado uniformemente (declaredValue = amount / totalItems) para que el
 * total al re-enviar coincida; el valor declarado por pieza individual NO es recuperable.
 * `productCode` tampoco viaja en los items → queda vacío.
 */
export function payloadJsonToShipmentGroup(
  payloadJson: string,
  ctx: ReverseShipmentContext
): ShipmentGroup {
  const { identificationTypes, isCOD = false } = ctx
  const p = JSON.parse(payloadJson)

  const isHome = !!p?.destination?.home_delivery
  const shipper = parseIdentification(
    p?.shipper?.identification,
    identificationTypes
  )
  const consignee = parseIdentification(
    p?.consignee?.identification,
    identificationTypes
  )

  // Teléfono: el forward guardó `${countryCode} ${numero}`. Recuperamos lo de después
  // del primer espacio (los dígitos sin el código de país).
  const rawPhone = (p?.consignee?.phone || '').trim()
  const spaceIdx = rawPhone.indexOf(' ')
  const consigneePhone =
    spaceIdx >= 0 ? rawPhone.slice(spaceIdx + 1).replace(/\s+/g, '') : rawPhone

  // Re-agrupar items idénticos → piezas con cantidad.
  const items: any[] = Array.isArray(p?.parcels?.items) ? p.parcels.items : []
  const totalItems = items.length
  const amount = Number(p?.declared_value?.amount) || 0
  const perItemValue = totalItems > 0 ? amount / totalItems : 0

  const pieceMap = new Map<string, ShipmentPieceRow>()
  for (const it of items) {
    const key = [
      it?.height,
      it?.width,
      it?.length,
      it?.weight,
      it?.content_category,
      it?.content_description,
    ].join('|')
    const existing = pieceMap.get(key)
    if (existing) {
      existing.quantity += 1
    } else {
      pieceMap.set(key, {
        quantity: 1,
        weight: Number(it?.weight) || 0,
        height: Number(it?.height) || 0,
        width: Number(it?.width) || 0,
        length: Number(it?.length) || 0,
        declaredValue: perItemValue,
        productName: it?.content_description || '',
        productCode: '',
        categoryId:
          it?.content_category != null ? String(it.content_category) : '49',
      })
    }
  }

  return {
    id: uuidv4(),
    shipmentHeaderReference: p?.partner_shipment_id || '',
    deliveryType: isHome ? DeliveryEnum.DELIVERY : DeliveryEnum.OFFICE,
    destinationCity: p?.destination?.location || '',
    storeCode: p?.destination?.store || '',
    // [F3] Preservar el origen elegido al reabrir una fila en corrección.
    originStoreCode: p?.origin?.store || undefined,
    // Idem el servicio: sin esto, corregir una fila reasignaba el primer servicio
    // autorizado y cambiaba en silencio el que se había elegido.
    serviceId: p?.service != null ? Number(p.service) : undefined,
    shipperDocType: shipper.docType,
    shipperDocNumber: shipper.docNumber,
    shipperName: p?.shipper?.name || '',
    consigneeDocType: consignee.docType,
    consigneeDocNumber: consignee.docNumber,
    consigneeName: p?.consignee?.name || '',
    consigneePhone,
    consigneeEmail: p?.consignee?.email || '',
    destinationAddress: p?.destination?.address?.line || '',
    postalCode: p?.destination?.address?.postal_code || '',
    notes: p?.notes || '',
    latitude: isHome ? (p?.destination?.coordinates?.latitude ?? null) : null,
    longitude: isHome ? (p?.destination?.coordinates?.longitude ?? null) : null,
    pieces: Array.from(pieceMap.values()),
    isShipperLocked: isCOD,
    isDuplicateInDB: false,
    externalManifest: p?.external_manifest || undefined,
  }
}
